// src/guides/useGuides.js
// The one hook every guide surface uses: merges the code-shipped default
// pack with the brand's override layer (Phase B adds Firestore docs as a
// third layer here), gates by role + brand features + gamification config,
// and hands back lookup helpers. All the logic lives in registry.js (pure,
// node-tested); this file only wires live context in.
//
// `loading` mirrors the gamification-config load so anchors don't flash in
// and then disappear when a requiresConfig gate resolves false.

import { useContext, useMemo } from 'react';

import { useAuth } from '../lib/auth';
import { normalizeRole } from '../lib/auth/roles';
import { EventContext } from '../event/eventContext';
import { getEventId } from '../event/paths';
import useGamificationConfig from '../hooks/useGamificationConfig';
import { DEFAULT_GUIDES, BRAND_GUIDE_OVERRIDES } from './defaultGuides';
import { resolveVisibleGuides, guidesForAnchor } from './registry';
import { buildGuideTokens } from './tokens';

export default function useGuides() {
  const { profile, roleForAccess } = useAuth() || {};
  const eventCtx = useContext(EventContext);
  const { config: gamificationConfig, loading: configLoading } = useGamificationConfig();

  // Same role resolution the dashboard uses for menu filtering.
  const role = normalizeRole(roleForAccess) || normalizeRole(profile?.type) || normalizeRole(profile?.role) || null;
  // Stable reference (dashboard idiom) so the memo below doesn't rebuild on
  // every render when the context object is the fallback.
  const eventConfig = useMemo(() => eventCtx?.config || eventCtx || {}, [eventCtx]);
  const eventId = getEventId();

  return useMemo(() => {
    const ctx = { role, eventConfig, gamificationConfig };
    const guides = configLoading ? [] : resolveVisibleGuides([DEFAULT_GUIDES, BRAND_GUIDE_OVERRIDES[eventId] || []], ctx);
    const tokens = buildGuideTokens({ eventConfig, gamificationConfig });
    return {
      guides,
      tokens,
      ctx,
      loading: configLoading,
      byId: (id) => guides.find((g) => g.id === id) || null,
      byAnchor: (anchor) => guidesForAnchor(guides, anchor)
    };
  }, [role, eventConfig, gamificationConfig, configLoading, eventId]);
}
